/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {
  ClickAwayListener,
  Paper,
  TextField
} from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {TopNavSearchIcon} from '@ion/dls/src/icons/TopNavSearchIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useInfiniteRolesQuery} from '@ion/query/src/hooks/useInfiniteRolesQuery';
import {
  SearchQueryValue,
  SuggestionsType
} from '../../search/types';
import RoleSearchDropdown from './RoleSearchDropdown';

export interface RoleSearchInputProps {
  accountId: number;
  placeholder: string;
  onMenuSelection: Function;
  accountTree: any;
  viewSubGroups: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    searchSuggestionInputWrapper : {
      maxWidth : '500px',
      width    : '100%',
      position : 'relative'
    },
    searchSuggestionInputRoot : {
      height             : '48px',
      '& .MuiInput-root' : {
        minHeight : '48px'
      },
      '& .MuiFormHelperText-root' : {
        position : 'absolute !important',
        bottom   : '-20px !important'
      }
    },
    searchSuggestionInputUnderline : {
      padding     : '12px !important',
      height      : 'inherit',
      maxHeight   : '48px',
      '&::before' : {
        borderBottom : 'transparent'
      },
      '&:hover' : {
        '&::before' : {
          borderBottom : `2px solid ${dlsColor('aaBlue')} !important`
        }
      },
      '& .MuiInput-root' : {
        minHeight : '48px'
      }
    },
    suggestionsWrapper : {
      zIndex    : 5,
      maxWidth  : 500,
      border    : 'none !important',
      top       : '65px !important',
      transform : 'translate3d(0px, 160px, 0px) !important',
      left      : 'unset !important',
      right     : 'unset !important',
      width     : '100%'
    },
    searchIcon : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    input : {
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    }
  };
});

/**
 * @RoleSearchInput
 * Is a component that allows the user to search for roles via the ModularSearchWidget.
 *
 * ```typescript
 * import RoleSearchInput from './roles/RoleSearchInput';
 *
 * ...
 *
 * <RoleSearchInput accountId={212900}
 *   placeholder={"Search for roles"}
 *   onMenuSelection={onMenuSelection}
 *   accountTree={accountTree}
 *   viewSubGroups={true} />
 *
 * ```
 *
 * @param accountId         - The account ID to search for roles
 * @param placeholder       - The placeholder text for the input field
 * @param onMenuSelection   - The function to call when a menu item is selected
 * @param accountTree       - The account tree
 * @param viewSubGroups     - A boolean to determine if subgroups should be included in the search
 */
const RoleSearchInput = ({
  accountId,
  placeholder,
  onMenuSelection,
  accountTree,
  viewSubGroups
}: RoleSearchInputProps) => {
  const classes                         = useStyles();
  const [searchString, setSearchString] = useState('');
  const [inputValue, setInputValue]     = useState('');
  const isValidStringLength             = searchString?.length >= 2;

  const onSelection = useCallback(function(item: string | SearchQueryValue) {
    return (event?: any) => {
      event?.preventDefault();
      onMenuSelection(item, SuggestionsType.LOOKUP, event);
    };
  }, []);

  const debouncedSetSearchString = useCallback(debounce((value: string) => {
    setSearchString(value);
  }, 500), []);

  const handleInputChange = function(event) {
    const newValue = event.target.value;
    setInputValue(newValue);
    debouncedSetSearchString(newValue);
  };

  const handleClickAway = useCallback(function() {
    setSearchString('');
    setInputValue('');
  }, []);

  const {
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading,
    data
  } = useInfiniteRolesQuery({
    accountId,
    searchString,
    includeSubGroups : viewSubGroups,
    enabled          : searchString?.length >= 2
  });

  const uniqueRoles = useCallback(() => {
    const map = new Map();

    data?.forEach(item => {
      const normalizedSearchTerm = item?.searchTerm?.trim()?.toLowerCase();

      (item?.name || [])
        ?.filter(role =>
          role?.title?.toLowerCase()?.includes(normalizedSearchTerm)
        )
        ?.forEach(role => {
          if (!map.has(entityId(role?.href))) {
            map.set(entityId(role?.href), {
              accountId : item?.accountId,
              id        : role?.title,
              name      : role?.title
            });
          }
        });
    });

    return Array.from(map.values());
  }, [data]);

  return (
    <div>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <TextField className={classes.searchSuggestionInputRoot}
            autoComplete="off"
            placeholder={placeholder}
            helperText={translate('common:modularsearch.groupsHelperText')}
            value={inputValue}
            onChange={handleInputChange}
            InputProps={{
              classes : {
                input     : classes.input,
                underline : classes.searchSuggestionInputUnderline
              },
              startAdornment : (
                <div className={classes.searchIcon}>
                  <TopNavSearchIcon />
                </div>
              )
            }}
            autoFocus />
          <Render.If condition={isValidStringLength}>
            <Popper open={isValidStringLength}
              className={classes.suggestionsWrapper}
              transition
              disablePortal>
              <Paper>
                <RoleSearchDropdown fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  onSelection={onSelection}
                  accountTree={accountTree}
                  isFetching={isFetchingNextPage}
                  isLoading={isLoading}
                  data={uniqueRoles()} />
              </Paper>
            </Popper>
          </Render.If>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default RoleSearchInput;
